// FILE: src/pages/ZReportListPage.jsx

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

// MUI Pagination imports
import MuiPagination from "@mui/material/Pagination";
import MuiPaginationItem from "@mui/material/PaginationItem";

import { formatMoney } from "../utils/formatMoney";

// -- STYLES ----------------------------------------------------------

// 1) Styled table containers
const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  overflow: "hidden",
}));

const StyledTableRow = styled(TableRow)(() => ({
  cursor: "pointer",
}));

const StyledTableHeadRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  "& .MuiTableCell-head": {
    color: theme.palette.common.white,
    fontWeight: "bold",
    padding: "4px",
  },
}));

const StyledTableCell = styled(TableCell)(() => ({
  padding: "4px",
  fontSize: "0.875rem",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

// 2) Custom pagination with a plain white background
const CustomPagination = styled(MuiPagination)(({ theme }) => ({
  "&.MuiPagination-root": {
    margin: theme.spacing(1),
    backgroundColor: "#fff", // Entire pagination bar is white
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  "& .MuiPaginationItem-root": {
    minWidth: 36,
    height: 36,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0, 0.5),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontWeight: 500,
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  "& .MuiPaginationItem-ellipsis": {
    color: theme.palette.text.secondary,
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    // Keep the selected item white, but emphasize with a border/text color
    backgroundColor: "#fff",
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    fontWeight: 600,
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    opacity: 0.6,
  },
}));

// -- COMPONENT -------------------------------------------------------

function ZReportListPage() {
  const navigate = useNavigate();

  // ZReport data
  const [zreports, setZreports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Pagination: default 10 rows per page
  const [page, setPage] = useState(0); // zero-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [accountId, setAccountId] = useState("all");

  // All accounts for the dropdown
  const [accounts, setAccounts] = useState([]);

  // Current user role
  const userRole = localStorage.getItem("userRole") || "";

  // Currency preference
  const [currency, setCurrency] = useState("USD");

  // 1) Fetch all accounts so user can filter by any account
  const fetchAllAccounts = useCallback(async () => {
    try {
      const res = await API.get("/accounts?limit=100000");
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("fetchAllAccounts Error:", err);
    }
  }, []);

  // 2) Fetch zreport lines from the server with all filters (server-based)
  const fetchZReports = useCallback(
    async (pageIndex, limit) => {
      setLoading(true);
      try {
        const actualPage = pageIndex + 1; // server is 1-based
        const params = {
          page: actualPage,
          limit,
        };
        if (accountId !== "all") params.accountId = accountId;
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (searchTerm.trim()) params.search = searchTerm.trim();

        const res = await API.get("/zreports", { params });
        const list = res.data.data || [];
        setZreports(list);
        setTotalCount(res.data.totalCount || 0);
      } catch (err) {
        console.error("ZReportListPage fetchZReports error:", err);
        alert("Failed to load ZReports with filters");
      } finally {
        setLoading(false);
      }
    },
    [accountId, startDate, endDate, searchTerm]
  );

  // 3) Initial load
  useEffect(() => {
    fetchAllAccounts();
    fetchZReports(page, rowsPerPage);
    (async () => {
      try {
        const userRes = await API.get("/users/me");
        setCurrency(userRes.data.currency || "USD");
      } catch (err) {
        console.error("Error fetching currency:", err);
      }
    })();
  }, [fetchAllAccounts, fetchZReports, page, rowsPerPage]);

  // Row color logic
  function getRowColor(z) {
    if (z.status === "pending") return "";
    if (z.c2bvariance !== 0) return "rgba(255, 0, 0, 0.15)"; // red highlight if variance
    return "rgba(0, 255, 0, 0.15)"; // green highlight if variance=0 and status=checked
  }

  // Actions
  function handleRowClick(zReportId) {
    navigate(`/zreport-details/${zReportId}`);
  }
  function handleRefresh() {
    fetchZReports(page, rowsPerPage);
  }
  function handleAddExpected() {
    navigate("/zreport-expected");
  }
  function handleAddActual() {
    navigate("/zreport-actual");
  }

  // If user can edit => user is Admin or Accountant, z.status === "pending"
  function canEdit(z) {
    if (!z) return false;
    const role = userRole.toLowerCase();
    if (role !== "admin" && role !== "accountant") return false;
    return z.status === "pending";
  }
  function handleEditClick(e, zLine) {
    e.stopPropagation();
    navigate(`/zreport-edit/${zLine._id}`);
  }

  // Searching & Filter
  const handleSearch = () => {
    setPage(0);
    fetchZReports(0, rowsPerPage);
  };
  const handleClearFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setAccountId("all");
    setPage(0);
    fetchZReports(0, rowsPerPage);
  };

  // 4) Pagination
  const handlePageChange = (event, newPage) => {
    // newPage is 1-based from <Pagination />
    setPage(newPage - 1);
  };
  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };
  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;

  return (
    <Box sx={{ p: 3, mt: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Z Report List
      </Typography>

      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: { xs: "center", sm: "space-between" },
        }}
      >
        {/* Left side: Buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={handleAddExpected}
            size="small"
            startIcon={<AddIcon />}
          >
            Add Expected
          </Button>
          <Button
            variant="contained"
            onClick={handleAddActual}
            size="small"
            startIcon={<AddIcon />}
          >
            Add Actual
          </Button>
        </Box>

        {/* Right side: Search, date range, account filter */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: "120px" }}
          />
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 110 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 110 }}
          />

          {/* Account filter: all or pick from dropdown */}
          <Autocomplete
            options={accounts}
            getOptionLabel={(option) =>
              `${option.accountName} (${option.accountNo})`
            }
            value={
              accountId === "all"
                ? null
                : accounts.find((acc) => acc._id === accountId) || null
            }
            onChange={(event, newValue) => {
              setAccountId(newValue ? newValue._id : "all");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Accounts"
                variant="outlined"
                size="small"
              />
            )}
            ListboxProps={{ style: { maxHeight: 200 } }}
            sx={{ minWidth: 150 }}
          />

          <IconButton color="primary" size="small" onClick={handleSearch}>
            <SearchIcon />
          </IconButton>

          {(searchTerm || startDate || endDate || accountId !== "all") && (
            <Button
              variant="text"
              color="secondary"
              size="small"
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{ borderRadius: 2, overflow: "hidden", boxShadow: 2 }}
        >
          <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
            <TableHead>
              <StyledTableHeadRow>
                <TableCell>Account</TableCell>
                <TableCell>Expected Date</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Target Cash</TableCell>
                <TableCell>Declared Cash</TableCell>
                <TableCell>Till Variance</TableCell>
                <TableCell>Expected Amount</TableCell>
                <TableCell>Actual Date</TableCell>
                <TableCell>Actual Amount</TableCell>
                <TableCell>c2bVariance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </StyledTableHeadRow>
            </TableHead>
            <TableBody>
              {zreports.length > 0 ? (
                zreports.map((z) => {
                  const rowBg = getRowColor(z);
                  return (
                    <StyledTableRow
                      key={z._id}
                      sx={{ backgroundColor: rowBg }}
                      onClick={() => handleRowClick(z._id)}
                    >
                      <StyledTableCell>
                        {z.account?.accountName || ""}
                      </StyledTableCell>
                      <StyledTableCell>
                        {z.expectedDate?.split("T")[0] || ""}
                      </StyledTableCell>
                      <StyledTableCell>
                        {z.category?.name || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatMoney(z.targetCash || 0, currency)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatMoney(z.declaredCash || 0, currency)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatMoney(z.tillVariance || 0, currency)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatMoney(z.expectedAmount || 0, currency)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {z.actualDate ? z.actualDate.split("T")[0] : ""}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatMoney(z.actualAmount || 0, currency)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatMoney(z.c2bvariance || 0, currency)}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={z.status || "N/A"}
                          color={z.status === "pending" ? "warning" : "success"}
                          variant="outlined"
                        />
                      </StyledTableCell>
                      <StyledTableCell
                        onClick={(e) => e.stopPropagation()}
                        align="center"
                      >
                        {canEdit(z) && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleEditClick(e, z)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={12} align="center">
                    No ZReport lines found.
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Custom pagination bar */}
          <Box
            sx={{
              p: 2,
              mt: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#fff", // Keep the bottom bar white
              borderTop: "1px solid #ddd",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {zreports.length} of {totalCount} records
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* Rows per page select */}
              <FormControl size="small" variant="outlined">
                <InputLabel>Rows</InputLabel>
                <Select
                  label="Rows"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{ width: 80 }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>

              <CustomPagination
                page={page + 1} // 1-based
                count={totalPages}
                onChange={handlePageChange}
                siblingCount={1}
                boundaryCount={1}
                renderItem={(item) => {
                  if (item.type === "previous") {
                    return (
                      <MuiPaginationItem {...item} disableRipple>
                        &lt;
                      </MuiPaginationItem>
                    );
                  }
                  if (item.type === "next") {
                    return (
                      <MuiPaginationItem {...item} disableRipple>
                        &gt;
                      </MuiPaginationItem>
                    );
                  }
                  return <MuiPaginationItem {...item} disableRipple />;
                }}
              />
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default ZReportListPage;
